<template>
  <div class="section-observations h-100">
    <div
      v-if="sampleObservation"
      class="h-100 d-flex flex-column justify-content-between"
    >
      <div class="d-flex flex-column flex-grow-1">
        <b-row class="d-flex align-items-center mb-2 mt-1 h-auto">
          <b-col>
            <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.observations') }}</span>
          </b-col>
          <b-col
            cols="auto"
            class="d-flex justify-content-end"
          >
            <b-button
              v-tooltip="$t('global.description.clear')"
              qa="section-observations-clear-button"
              :disabled="disabled"
              class="btn__icon btn__icon--primary  px-2"
              @click="cleanSampleObservationText"
            >
              <i
                class="far fa-eraser"
                aria-hidden="true"
              />
            </b-button>
          </b-col>
        </b-row>
        <b-row class="diagnosis-layout-height flex-grow-1">
          <b-col>
            <b-form-textarea
              ref="sampleObservationsTextArea"
              :key="refreshEditor"
              v-model="sampleObservation.text"
              class="w-100 observations-textarea"
              :disabled="disabled"
              no-resize
            />
          </b-col>
        </b-row>
      </div>
      <div class="d-flex justify-content-end">
        <vt-loading-button
          v-if="$_canEditSample()"
          :disabled="disabled"
          class="btn__outline btn-sm mt-2"
          qa="section-observations-save-button"
          :params="{ promise: save, shortkeys: ['alt', 's'] }"
        >
          <i
            class="fal fa-save mr-2"
            aria-hidden="true"
          />
          {{ $t('global.save') }}
        </vt-loading-button>
      </div>
    </div>
  </div>
</template>

<script>
import sampleObservationsService from '@/api/resources/clob/SampleObservationsService'
import { cloneDeep, isEqual } from 'lodash'
import { SampleOperationGuardMixin } from '@/api/operations/mixins/guards/sample-operation-guard.mixin'

export default {
    name: 'SectionObservations',
    mixins: [
        SampleOperationGuardMixin
    ],
    props: {
        labId: { type: Number, required: true },
        samId: { type: Number, required: true },
        casId: { type: Number, required: true },
        disabled: { type: Boolean, required: true }
    },
    data() {
        return {
            sampleObservation: null,
            originalSampleObservation: null,
            refreshEditor: 0
        }
    },
    async mounted() {
        await this.loadObservation()

        const sampleObservationText = this.sampleObservation?.text
        if (sampleObservationText) {
            this.$_moveInputElementCaretToEnd(this.$refs.sampleObservationsTextArea, sampleObservationText?.length)
        }
    },
    methods: {
        async reloadTab() {
            await this.loadObservation()
        },
        async loadObservation() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(sampleObservationsService.findById(this.labId, this.casId, this.samId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.sampleObservation = response
            this.originalSampleObservation = cloneDeep(response)
        },
        hasBeenChanged() {
            return !isEqual(this.sampleObservation, this.originalSampleObservation)
        },
        cleanSampleObservationText() {
            this.sampleObservation.text = ''
            this.refreshEditor++
        },
        async save() {
            this.updateLoading(true)
            const observation = {
                text: this.sampleObservation.text,
                originalConcurrencyUpdatedAt: this.sampleObservation.originalConcurrencyUpdatedAt
            }
            const [err] = await this.toCall(sampleObservationsService.update(this.labId, this.casId, this.samId, observation))
            this.updateLoading(false)
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.$t('diagnosis.sections.observations'), err, () => {
                        this.sampleObservation.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        this.save()
                    })
                } else {
                    this.showErrorModalGlobal()
                    return false
                }
            } else {
                this.showToastSuccessGlobal()
                this.$emit('reloadSummary')
                await this.loadObservation()
                return true
            }
        }
    }
}
</script>

<style lang="scss">
.section-observations .ql-toolbar.ql-snow {
  padding: 2px;
}

.section-observations .ql-editor {
  height: calc(100vh - 365px);
  transition: 0.35s;
  overflow-y: auto;
}

.section-observations .observations-textarea {
    height: 100%;
    border-radius: 0;
    border-color: vt-color(general-border);
}

.section-observations .diagnosis-layout-height {
    overflow-y: auto;
}

/*--Styles for large screen--*/
@media (max-width: 1300px) {
  .section-observations .ql-editor {
  height: calc(100vh - 380px);
}
}

</style>
